/**
 * AppsView — Central app launcher & service discovery dashboard
 *
 * Queries /api/apps (backed by Maintainr heartbeats) to show all running
 * OpCentral apps with their status, version, and instance details.
 * Click an app to open it or select between instances (dev vs prod).
 *
 * CHANGE HISTORY:
 * - 2026-02-26 danielbeer: Initial implementation
 */

import { useState, useEffect, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AppInstance {
  id: string;
  name: string;
  type: string;
  status: 'online' | 'offline' | 'stopping' | 'unknown';
  hostname: string;
  port: number;
  url: string;
  version: string;
  capabilities: string[];
  environment: string;
  lastSeen: string;
  pid: number;
}

interface AppEntry {
  name: string;
  type: string;
  instances: AppInstance[];
  primaryUrl: string;
  latestVersion: string;
  status: 'online' | 'degraded' | 'offline';
}

interface AppsResponse {
  apps: AppEntry[];
  maintainrUrl: string;
  totalInstances: number;
  totalApps: number;
  timestamp: string;
}

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchApps(): Promise<AppsResponse> {
  const res = await fetch('/api/apps');
  if (!res.ok) throw new Error(`Failed to fetch apps: ${res.status}`);
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusIcon(status: string): string {
  switch (status) {
    case 'online':
      return '\u25CF'; // filled circle
    case 'degraded':
      return '\u25D0'; // half circle
    case 'offline':
      return '\u25CB'; // empty circle
    case 'stopping':
      return '\u25D2'; // bottom half circle
    default:
      return '?';
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'online':
      return '#4caf50';
    case 'degraded':
      return '#ff9800';
    case 'offline':
      return '#9e9e9e';
    case 'stopping':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
}

function timeAgo(isoDate: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

// ── Instance selector modal ──────────────────────────────────────────────────

function InstancePicker({ app, onClose }: { app: AppEntry; onClose: () => void }) {
  const openInstance = (inst: AppInstance) => {
    if (inst.url) window.open(inst.url, '_blank');
  };

  return (
    <div className="amp-apps-overlay" onClick={onClose}>
      <div className="amp-apps-modal" onClick={(e) => e.stopPropagation()}>
        <div className="amp-apps-modal__header">
          <h3>{app.name}</h3>
          <span className="amp-apps-modal__count">
            {app.instances.length} instance{app.instances.length !== 1 ? 's' : ''}
          </span>
          <button className="amp-apps-modal__close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="amp-apps-modal__list">
          {app.instances.map((inst) => (
            <div
              key={inst.id}
              className="amp-apps-instance"
              data-status={inst.status}
              onClick={() => openInstance(inst)}
            >
              <div className="amp-apps-instance__main">
                <span
                  className="amp-apps-instance__dot"
                  style={{ color: statusColor(inst.status) }}
                >
                  {statusIcon(inst.status)}
                </span>
                <span className="amp-apps-instance__host">
                  {inst.hostname}:{inst.port}
                </span>
                <span className="amp-apps-instance__env" data-env={inst.environment}>
                  {inst.environment}
                </span>
              </div>
              <div className="amp-apps-instance__meta">
                <span>v{inst.version}</span>
                <span>PID {inst.pid}</span>
                <span>{timeAgo(inst.lastSeen)}</span>
              </div>
              {inst.capabilities.length > 0 && (
                <div className="amp-apps-instance__caps">
                  {inst.capabilities.map((cap) => (
                    <span key={cap} className="amp-apps-chip">
                      {cap}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── App Card ─────────────────────────────────────────────────────────────────

function AppCard({ app, onSelect }: { app: AppEntry; onSelect: (app: AppEntry) => void }) {
  const handleClick = () => {
    if (
      app.instances.length === 1 &&
      app.instances[0].url &&
      app.instances[0].status === 'online'
    ) {
      // Single online instance — open directly
      window.open(app.instances[0].url, '_blank');
    } else {
      // Multiple instances or none online — show picker
      onSelect(app);
    }
  };

  const onlineCount = app.instances.filter((i) => i.status === 'online').length;
  const primary = app.instances[0];

  return (
    <div className="amp-apps-card" data-status={app.status} onClick={handleClick}>
      <div className="amp-apps-card__header">
        <span className="amp-apps-card__status" style={{ color: statusColor(app.status) }}>
          {statusIcon(app.status)}
        </span>
        <h3 className="amp-apps-card__name">{app.name}</h3>
        <span className="amp-apps-card__type">{app.type}</span>
      </div>
      <div className="amp-apps-card__body">
        <div className="amp-apps-card__version">v{app.latestVersion}</div>
        <div className="amp-apps-card__instances">
          {onlineCount}/{app.instances.length} online
        </div>
        {primary && <div className="amp-apps-card__url">{primary.url || 'no url'}</div>}
      </div>
      {primary?.capabilities && primary.capabilities.length > 0 && (
        <div className="amp-apps-card__caps">
          {primary.capabilities.slice(0, 4).map((cap) => (
            <span key={cap} className="amp-apps-chip">
              {cap}
            </span>
          ))}
          {primary.capabilities.length > 4 && (
            <span className="amp-apps-chip">+{primary.capabilities.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export function AppsView() {
  const [data, setData] = useState<AppsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<AppEntry | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const result = await fetchApps();
      setData(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + auto-refresh every 30s
  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const apps = data?.apps ?? [];
  const filtered = filter
    ? apps.filter(
        (a) =>
          a.name.toLowerCase().includes(filter.toLowerCase()) ||
          a.type.toLowerCase().includes(filter.toLowerCase()) ||
          a.instances.some((i) =>
            i.capabilities.some((c) => c.toLowerCase().includes(filter.toLowerCase())),
          ),
      )
    : apps;

  const online = apps.filter((a) => a.status === 'online').length;
  const degraded = apps.filter((a) => a.status === 'degraded').length;
  const offline = apps.filter((a) => a.status === 'offline').length;

  return (
    <div className="amp-apps">
      {/* Header bar */}
      <div className="amp-apps-header">
        <h2 className="amp-apps-title">Applications</h2>
        <div className="amp-apps-stats">
          <span className="amp-apps-stat" style={{ color: '#4caf50' }}>
            {online} online
          </span>
          {degraded > 0 && (
            <span className="amp-apps-stat" style={{ color: '#ff9800' }}>
              {degraded} degraded
            </span>
          )}
          {offline > 0 && (
            <span className="amp-apps-stat" style={{ color: '#9e9e9e' }}>
              {offline} offline
            </span>
          )}
          <span className="amp-apps-stat amp-apps-stat--total">
            {data?.totalInstances ?? 0} instances
          </span>
        </div>
        <div className="amp-apps-controls">
          <input
            className="amp-apps-search"
            type="text"
            placeholder="Filter apps..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <button className="amp-apps-refresh" onClick={load} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="amp-apps-error">
          <strong>Maintainr unreachable:</strong> {error}
          <br />
          <small>
            Make sure maintainr-lib is running on {data?.maintainrUrl ?? 'http://localhost:6300'}
          </small>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div className="amp-apps-empty">
          {filter ? (
            <p>No apps matching &ldquo;{filter}&rdquo;</p>
          ) : (
            <>
              <p>No applications registered yet.</p>
              <p className="amp-apps-empty__hint">
                Apps using appr-lib automatically register with Maintainr on startup.
                <br />
                Start an app to see it appear here.
              </p>
            </>
          )}
        </div>
      )}

      {/* App grid */}
      <div className="amp-apps-grid">
        {filtered.map((app) => (
          <AppCard key={app.name} app={app} onSelect={setSelectedApp} />
        ))}
      </div>

      {/* Instance picker */}
      {selectedApp && <InstancePicker app={selectedApp} onClose={() => setSelectedApp(null)} />}

      {/* Footer */}
      {data && (
        <div className="amp-apps-footer">
          Last updated: {new Date(data.timestamp).toLocaleTimeString()}
          &nbsp;&middot;&nbsp;Auto-refreshes every 30s
        </div>
      )}
    </div>
  );
}
